import clsx from "clsx";
import { Check, CircleAlert, Loader2 } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useId, useState } from "react";

/*
 * One saving indicator for a whole area (a record window) instead of one per field. Fields report
 * how their save is going (useReportSave); the area adds them up (useSaveSummary) and shows it in
 * one place (SaveIndicator): "Saving…", then "Saved", or "Couldn't save" while any field's save
 * was turned down (that field still says why, under it). Outside such an area nothing collects
 * the reports, and each field shows its own mark beside its label.
 */

export type SaveKind = "idle" | "saving" | "saved" | "error";

type Report = (id: string, kind: SaveKind) => void;
const ReportContext = createContext<Report | null>(null);

/** Tell the area around this field how its save is going. True when the area shows it (so the field doesn't). */
export function useReportSave(kind: SaveKind): boolean {
    const report = useContext(ReportContext);
    const id = useId();
    useEffect(() => {
        report?.(id, kind);
    }, [report, id, kind]);
    // Gone (e.g. its tab closed): it's not saving any more as far as the area's concerned.
    useEffect(() => () => report?.(id, "idle"), [report, id]);
    return report !== null;
}

/** Most important first: anything turned down, then anything saving, then anything just saved. */
const summarise = (kinds: Iterable<SaveKind>): SaveKind => {
    const all = new Set(kinds);
    if (all.has("error")) return "error";
    if (all.has("saving")) return "saving";
    return all.has("saved") ? "saved" : "idle";
};

/** For an area showing one indicator: how its fields' saves are going, and `report` for its SaveArea to collect them. */
export function useSaveSummary() {
    const [kinds, setKinds] = useState<ReadonlyMap<string, SaveKind>>(new Map());
    const report = useCallback<Report>((id, kind) => {
        setKinds((current) => {
            if ((current.get(id) ?? "idle") === kind) return current;
            const next = new Map(current);
            if (kind === "idle") next.delete(id);
            else next.set(id, kind);
            return next;
        });
    }, []);
    return { summary: summarise(kinds.values()), report };
}

/** The fields inside report to `report` (from useSaveSummary) instead of showing their own marks. */
export function SaveArea({ report, children }: Readonly<{ report: Report; children: ReactNode }>) {
    return <ReportContext value={report}>{children}</ReportContext>;
}

const SHOWN: Record<Exclude<SaveKind, "idle">, { icon: ReactNode; text: string }> = {
    saving: { icon: <Loader2 className="size-3.5 animate-spin" />, text: "Saving…" },
    saved: { icon: <Check className="size-3.5" />, text: "Saved" },
    error: { icon: <CircleAlert className="size-3.5" />, text: "Couldn't save" },
};

/** The summary, in a space that's always kept (so nothing beside it moves). On a coloured bar: white text, a white badge for errors. */
export function SaveIndicator({ kind }: Readonly<{ kind: SaveKind }>) {
    const shown = kind === "idle" ? null : SHOWN[kind];
    return (
        <span className="inline-flex w-24 shrink-0 justify-end" aria-live="polite">
            {shown ? (
                <span
                    className={clsx(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap",
                        kind === "error" ? "bg-white font-semibold text-danger" : "text-white/90",
                    )}
                >
                    {shown.icon}
                    {shown.text}
                </span>
            ) : null}
        </span>
    );
}
